import { useEffect, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import { debounce } from 'lodash';
import { AuthStatus } from 'constants/auth';
import { hasLocationPermission } from 'utils/client/hasLocationPermission';
import { requestUserLocation } from 'utils/client/requestUserLocation';
import { extractAddressParts } from 'utils/shared/geo/extractAddressParts';
import { Position, useGeoLocationState } from 'context/GeoLocationContext';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useViewer } from './useViewer';

const DEFAULT_AUTOCOMPLETE_DEBOUNCE_TIME = 400;
const IS_CAPTURING_ERRORS = false;

interface Props {
  autocompleteDebounceTime?: number;
}

export const useGeoLocation = (
  { autocompleteDebounceTime = DEFAULT_AUTOCOMPLETE_DEBOUNCE_TIME }: Props = {
    autocompleteDebounceTime: DEFAULT_AUTOCOMPLETE_DEBOUNCE_TIME,
  },
) => {
  const [suggestedAddresses, setSuggestedAddresses] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  const [activeAutocompleteAddress, setActiveAutocompleteAddress] =
    useState<google.maps.places.AutocompletePrediction | null>(null);
  const geolocationState = useGeoLocationState();
  const {
    addressString,
    setPosition,
    setExactAddress,
    setErrorRequestLocation,
    setIsExactCenter,
    setAddressString,
  } = geolocationState;

  const user = useGetCurrentUser();
  const viewer = useViewer();
  const geometry = user.user?.geometry;

  const getAddressFromLatLng = async (latitude: number, longitude: number) => {
    try {
      if (window.google && window.google.maps) {
        return new Promise((resolve) => {
          const geocoder = new window.google.maps.Geocoder();

          geocoder.geocode(
            { location: { lat: latitude, lng: longitude } },
            (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
              if (status === 'OK' && results && results.length > 0) {
                const addressParts = extractAddressParts(results[0]);
                const addressString = `${addressParts.city}, ${addressParts.state} ${addressParts.zipcode}`;
                setAddressString(addressString);
                resolve(addressString);
              } else {
                console.error('Geocoding failed due to: ' + status);

                setAddressString('Location selected');
                resolve(null);
              }
            },
          );
        });
      }

      // Fall back to the original fetch method if Google Maps API isn't available
      // This maintains backward compatibility
      const addressFromLatLngResponse = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.GOOGLE_MAPS_API_KEY}`,
      );
      const addressFromLatLng = await addressFromLatLngResponse.json();

      if (addressFromLatLng.results && addressFromLatLng.results.length > 0) {
        const addressParts = extractAddressParts(addressFromLatLng.results[0]);
        const addressString = `${addressParts.city}, ${addressParts.state} ${addressParts.zipcode}`;
        setAddressString(addressString);
        return addressString;
      } else if (addressFromLatLng.status === 'REQUEST_DENIED') {
        console.warn('Geocoding API access denied. Check API key restrictions.');
        setAddressString('Location selected');
        return null;
      }

      return null;
    } catch (error) {
      console.error('Error in getAddressFromLatLng:', error);
      // Provide a fallback in case of error
      setAddressString('Location selected');
      return null;
    }
  };
  const requestUserLocationAndUpdateState = async () => {
    return requestUserLocation()
      .then((geo) => {
        return new Promise((resolve: (value: Position | null) => any) => {
          if (geo) {
            const latitude = geo.coords.latitude;
            const longitude = geo.coords.longitude;
            const position = {
              latitude: latitude,
              longitude: longitude,
            };
            setPosition(position);
            setIsExactCenter(true);
            getAddressFromLatLng(latitude, longitude);

            return resolve(position);
          } else if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (geo) => {
                const latitude = geo.coords.latitude;
                const longitude = geo.coords.longitude;
                const position = {
                  latitude: latitude,
                  longitude: longitude,
                };

                setPosition(position);
                setIsExactCenter(true);

                return resolve(position);
              },
              (error) => {
                setErrorRequestLocation(error);
                return resolve(null);
              },
            );
          } else {
            return resolve(null);
          }
        });
      })
      .catch((error) => {
        /**
         * @todo should this be separate error from the estimated location?
         */
        setErrorRequestLocation(error);
        return null;
      });
  };

  const getEstimatedLocation = async () => {
    return (
      fetch(
        `https://www.googleapis.com/geolocation/v1/geolocate?key=${process.env.GOOGLE_MAPS_API_KEY}`,
        {
          method: 'POST',
        },
      )
        .then((response) => response.json())
        .then(async (data) => {
          const latitude = data.location.lat;
          const longitude = data.location.lng;
          const position = {
            latitude: latitude,
            longitude: longitude,
          };
          setPosition(position);
          getAddressFromLatLng(latitude, longitude);

          return position;
        })
        /**
         * @todo should this be separate error from the exact location?
         */
        .catch((error) => {
          setErrorRequestLocation(error);
          return null;
        })
    );
  };

  const getImmediateAutcompletePlaces = async (
    input: string,
  ): Promise<google.maps.places.AutocompletePrediction[]> => {
    return new Promise((resolve) => {
      if (!window.google) {
        console.error('Google Maps API not loaded');
        return;
      }

      const options = {};

      const autocompleteService = new window.google.maps.places.AutocompleteService();

      autocompleteService.getPlacePredictions(
        { input, ...options },
        (predictions: any, status: any) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
            if (IS_CAPTURING_ERRORS) {
              captureException(new Error('Google Maps Error:' + ' ' + status));
            }
            return;
          }

          setSuggestedAddresses(predictions || []);
          resolve(predictions || []);
        },
      );
    });
  };

  const getAutocompletePlaces = debounce(getImmediateAutcompletePlaces, autocompleteDebounceTime);

  const getExactDetails = (placeId: string): Promise<google.maps.places.PlaceResult | null> => {
    return new Promise((resolve) => {
      const service = new google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({ placeId: placeId }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else {
          if (IS_CAPTURING_ERRORS) {
            captureException(new Error('Google Maps Error:' + ' ' + status));
          }
          resolve(null);
        }
      });
    });
  };

  const handleSubmitAutocomplete = async (
    address?: google.maps.places.AutocompletePrediction | null,
  ) => {
    setAddressString(address?.description || '');

    try {
      let addressForFetch = address;

      if (!addressForFetch) {
        const finalAddress = await getImmediateAutcompletePlaces(addressString);
        addressForFetch = finalAddress[0];
        setActiveAutocompleteAddress(addressForFetch);
      }

      const exactAddressResp = await getExactDetails(addressForFetch?.place_id || '');

      if (!exactAddressResp || !exactAddressResp.geometry || !exactAddressResp.geometry.location) {
        return;
      }

      setExactAddress(exactAddressResp);

      const newLatitude = exactAddressResp.geometry.location.lat();
      const newLongitude = exactAddressResp.geometry.location.lng();
      const position = {
        latitude: newLatitude,
        longitude: newLongitude,
      };
      setPosition(position);
      setIsExactCenter(true);

      return position;
    } catch (error) {
      captureException(error);
      return null;
    }
  };

  const handleAutcompleteSuggestions = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setAddressString(event.target.value);
    setActiveAutocompleteAddress(null);

    if (event.target.value.length > 2) {
      const predictions = await getAutocompletePlaces(event.target.value);
      setSuggestedAddresses(predictions || []);
    }
  };

  useEffect(() => {
    const initializeLocation = async () => {
      if (viewer.isSessionLoading) {
        return;
      }

      let hasPermission = false;
      try {
        hasPermission = await hasLocationPermission();
      } catch (error) {
        console.error('Error checking location permission:', error);
      }

      if (hasPermission) {
        try {
          const precisePosition = await requestUserLocationAndUpdateState();
          if (precisePosition) {
            return;
          }
        } catch (error) {
          console.error('Error getting precise location:', error);
        }
      }

      if (viewer.userId && geometry && geometry.coordinates && geometry.coordinates.length === 2) {
        const latitude = geometry.coordinates[1];
        const longitude = geometry.coordinates[0];
        const position = {
          latitude: latitude,
          longitude: longitude,
        };

        setPosition(position);
        setIsExactCenter(true);
        getAddressFromLatLng(latitude, longitude);
      } else if (!viewer.userId) {
        await getEstimatedLocation();
      }
    };

    !geolocationState.position && initializeLocation();
  }, [viewer.userId, geometry, viewer.isSessionLoading, geolocationState.position]);

  return {
    requestUserLocation: requestUserLocationAndUpdateState,

    handleAutcompleteSuggestions,
    hasLocationPermission,

    ...geolocationState,
    // New autocomplete state with fallback values
    isLoadingAutocomplete: false,
    autocompleteInputLength: 0,
    autocompleteMinLength: 3,
  };
};
